/**
 * Google Drive API service.
 * Uses Google Workspace MCP Gateway for data fetching.
 * Interfaces and helpers remain unchanged for panel compatibility.
 */
import { mcpCallOrThrow, isMcpConnected } from './googleMcp'
const DRIVE_API = 'https://www.googleapis.com/drive/v3'
export interface DriveFile {
  id: string
  name: string
  mimeType: string
  webViewLink?: string
  webContentLink?: string
  iconLink?: string
  thumbnailLink?: string
  createdTime: string
  modifiedTime: string
  size?: string
  starred: boolean
  trashed: boolean
  owners?: Array<{
    displayName: string
    emailAddress: string
    photoLink?: string
  }>
  lastModifyingUser?: {
    displayName: string
    emailAddress: string
    photoLink?: string
  }
  shared: boolean
  parents?: string[]
}
interface DriveListResponse {
  files: DriveFile[]
  nextPageToken?: string
  incompleteSearch?: boolean
}

/** Map MIME types to friendly labels */
export function getDriveFileType(mimeType: string): {
  label: string
  color: string
  icon: string
} {
  const types: Record<
    string,
    {
      label: string
      color: string
      icon: string
    }
  > = {
    'application/vnd.google-apps.document': {
      label: 'Doc',
      color: 'text-blue-400',
      icon: '📄',
    },
    'application/vnd.google-apps.spreadsheet': {
      label: 'Sheet',
      color: 'text-emerald-400',
      icon: '📊',
    },
    'application/vnd.google-apps.presentation': {
      label: 'Slides',
      color: 'text-amber-400',
      icon: '📽️',
    },
    'application/vnd.google-apps.folder': {
      label: 'Folder',
      color: 'text-slate-400',
      icon: '📁',
    },
    'application/vnd.google-apps.form': {
      label: 'Form',
      color: 'text-purple-400',
      icon: '📋',
    },
    'application/pdf': {
      label: 'PDF',
      color: 'text-red-400',
      icon: '📕',
    },
    'image/png': {
      label: 'Image',
      color: 'text-pink-400',
      icon: '🖼️',
    },
    'image/jpeg': {
      label: 'Image',
      color: 'text-pink-400',
      icon: '🖼️',
    },
  }
  return (
    types[mimeType] || {
      label: 'File',
      color: 'text-slate-400',
      icon: '📎',
    }
  )
}
export const googleDrive = {
  /**
   * List recent files (sorted by last modified).
   */
  async getRecentFiles(maxResults: number = 20): Promise<DriveFile[]> {
    if (!isMcpConnected())
      throw new Error(
        'Google no está conectado. Conectá tu cuenta desde Conectores.',
      )
    const result = await mcpCallOrThrow<any>('drive_list_files', {
      query: 'trashed = false',
      max_results: maxResults,
      order_by: 'modifiedTime desc',
    })
    return normalizeDriveFiles(result, 'getRecentFiles')
  },
  /**
   * Search files by name or content.
   */
  async searchFiles(
    query: string,
    maxResults: number = 20,
  ): Promise<DriveFile[]> {
    if (!isMcpConnected())
      throw new Error(
        'Google no está conectado. Conectá tu cuenta desde Conectores.',
      )
    const escapedQuery = query.replace(/'/g, "\\'")
    const result = await mcpCallOrThrow<any>('drive_search_files', {
      query: escapedQuery,
      max_results: maxResults,
    })
    return normalizeDriveFiles(result, 'searchFiles')
  },
  /**
   * Get files shared with the user.
   */
  async getSharedFiles(maxResults: number = 20): Promise<DriveFile[]> {
    if (!isMcpConnected())
      throw new Error(
        'Google no está conectado. Conectá tu cuenta desde Conectores.',
      )
    const result = await mcpCallOrThrow<any>('drive_list_shared', {
      max_results: maxResults,
    })
    return normalizeDriveFiles(result, 'getSharedFiles')
  },
  /**
   * Get starred files.
   */
  async getStarredFiles(maxResults: number = 20): Promise<DriveFile[]> {
    if (!isMcpConnected())
      throw new Error(
        'Google no está conectado. Conectá tu cuenta desde Conectores.',
      )
    const result = await mcpCallOrThrow<any>('drive_list_files', {
      query: 'trashed = false and starred = true',
      max_results: maxResults,
      order_by: 'modifiedTime desc',
    })
    return normalizeDriveFiles(result, 'getStarredFiles')
  },
  /**
   * Get files by specific MIME type.
   */
  async getFilesByMimeType(
    mimeType: string,
    maxResults: number = 20,
  ): Promise<DriveFile[]> {
    if (!isMcpConnected())
      throw new Error(
        'Google no está conectado. Conectá tu cuenta desde Conectores.',
      )
    const result = await mcpCallOrThrow<any>('drive_list_files', {
      query: `trashed = false and mimeType = '${mimeType}'`,
      max_results: maxResults,
      order_by: 'modifiedTime desc',
    })
    return normalizeDriveFiles(result, 'getFilesByMimeType')
  },
  /**
   * List files inside a specific folder (or root).
   */
  async getFilesInFolder(
    folderId: string = 'root',
    maxResults: number = 50,
  ): Promise<DriveFile[]> {
    if (!isMcpConnected())
      throw new Error(
        'Google no está conectado. Conectá tu cuenta desde Conectores.',
      )
    const result = await mcpCallOrThrow<any>('drive_list_files', {
      query: `trashed = false and '${folderId}' in parents`,
      max_results: maxResults,
      order_by: 'folder,name',
    })
    return normalizeDriveFiles(result, 'getFilesInFolder')
  },
  /**
   * Get Drive storage quota info.
   */
  async getStorageQuota(): Promise<{
    usage: number
    limit: number
  }> {
    if (!isMcpConnected())
      throw new Error(
        'Google no está conectado. Conectá tu cuenta desde Conectores.',
      )
    const result = await mcpCallOrThrow<any>('drive_get_storage_quota')
    const quota = result?.storageQuota || result?.data?.storageQuota || result
    return {
      usage: parseInt(quota?.usage || '0'),
      limit: parseInt(quota?.limit || '0'),
    }
  },
}

/** Get download URL for a file */
export function getDriveDownloadUrl(file: DriveFile): string | null {
  // Google native files need export URLs
  if (file.mimeType.includes('document')) {
    return `https://docs.google.com/document/d/${file.id}/export?format=pdf`
  }
  if (file.mimeType.includes('spreadsheet')) {
    return `https://docs.google.com/spreadsheets/d/${file.id}/export?format=xlsx`
  }
  if (file.mimeType.includes('presentation')) {
    return `https://docs.google.com/presentation/d/${file.id}/export/pptx`
  }
  if (file.mimeType.includes('form')) {
    return null // Forms can't be downloaded
  }
  if (file.mimeType.includes('folder')) {
    return null // Folders can't be downloaded
  }
  // Non-Google files use webContentLink
  return file.webContentLink || null
}

/** Get embed preview URL for a file */
export function getDriveEmbedUrl(file: DriveFile): string | null {
  if (file.mimeType.includes('document')) {
    return `https://docs.google.com/document/d/${file.id}/preview`
  }
  if (file.mimeType.includes('spreadsheet')) {
    return `https://docs.google.com/spreadsheets/d/${file.id}/preview`
  }
  if (file.mimeType.includes('presentation')) {
    return `https://docs.google.com/presentation/d/${file.id}/embed?start=false`
  }
  if (file.mimeType.includes('form')) {
    return `https://docs.google.com/forms/d/${file.id}/viewform?embedded=true`
  }
  if (file.mimeType === 'application/pdf' && file.webViewLink) {
    return file.webViewLink
  }
  return null
}

/** Normalize MCP response to DriveFile array */
function normalizeDriveFiles(result: any, method: string): DriveFile[] {
  if (Array.isArray(result)) return result
  if (result?.files && Array.isArray(result.files)) return result.files
  if (result?.items && Array.isArray(result.items)) return result.items
  if (result?.data && Array.isArray(result.data)) return result.data
  if (result?.data?.files && Array.isArray(result.data.files))
    return result.data.files
  console.warn(
    `[GoogleDrive] ${method} unexpected response, keys:`,
    result ? Object.keys(result) : 'null',
  )
  return []
}
